import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth, authHeaders } from "../contexts/AuthContext";
import { DraftProvider, useDraft } from "../contexts/DraftContext";
import { DraftFiltersProvider } from "../contexts/DraftFiltersContext";
import { useAdamVoiceGate } from "../hooks/useAdamVoiceGate";
import { setAudioVolume } from "../audio/auctioneerAudio";
import { loadAudioPrefs, saveAudioPrefs } from "../audio/audioPrefs";
import { History as HistoryIcon, Users as UsersIcon } from "lucide-react";

import UnifiedDraftShell from "../components/draft/UnifiedDraftShell";
import AuctionPlayerPool from "../components/draft/AuctionPlayerPool";
import CompactActionPanel from "../components/draft/CompactActionPanel";
import PlayerAnalysisCanvas from "../components/draft/PlayerAnalysisCanvas";
import ChatPanelWithFilter from "../components/draft/ChatPanelWithFilter";
import RosterPanel from "../components/draft/RosterPanel";
import TabbedPanel from "../components/draft/TabbedPanel";
import PickHistoryTabBody from "../components/draft/PickHistoryTabBody";
import MobilePaddle from "../components/draft/MobilePaddle";
import CommissionerControls from "../components/draft/CommissionerControls";
import MockDraftControls from "../components/draft/MockDraftControls";
import DraftRoomAbandonButton from "../components/draft/DraftRoomAbandonButton";
import CommissionerManagersDropdown from "../components/draft/CommissionerManagersDropdown";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function DraftRoomInner({ leagueId, league }) {
  const { user } = useAuth();
  const {
    draftState, isConnected, auctioneerMessage, bridgeRecapUrl,
    mockDraftCompleteEvent, setMockDraftCompleteEvent,
    recapDismissed, dismissRecap, activityVoice,
    fullPickHistory,
  } = useDraft();
  const isCommissioner = league?.commissioner_id === user?.id;

  // [Phase 2c.6-A.1.5] Audio controls - auctioneer TTS playback + LiveActivityFeed header.
  // [TD-73 Section 2.9] Hydrate from pgf_audio_prefs:{user_id} so the user's
  // lobby choices carry into the draft room. Write-back on every change.
  const [muted, setMuted] = useState(() => {
    const p = loadAudioPrefs(user?.id);
    return p ? p.tts_enabled === false : false;
  });
  const [volume, setVolume] = useState(() => {
    const p = loadAudioPrefs(user?.id);
    return Math.round(((p?.tts_volume) ?? 1) * 100);
  });
  // Apply hydrated volume to the auctioneerAudio singleton on first mount.
  useEffect(() => {
    setAudioVolume((muted ? 0 : volume) / 100);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const handleVolumeChange = (e) => {
    const v = parseInt(e.target.value, 10);
    setVolume(v);
    setAudioVolume(v / 100);
    if (v === 0 && !muted) setMuted(true);
    if (v > 0 && muted) setMuted(false);
    saveAudioPrefs(user?.id, { tts_volume: v / 100, tts_enabled: v > 0 });
  };
  const toggleMute = () => {
    if (muted) {
      const restore = volume > 0 ? volume : 80;
      setMuted(false);
      setVolume(restore);
      setAudioVolume(restore / 100);
      saveAudioPrefs(user?.id, { tts_enabled: true, tts_volume: restore / 100 });
    } else {
      setMuted(true);
      setAudioVolume(0);
      saveAudioPrefs(user?.id, { tts_enabled: false });
    }
  };
  useAdamVoiceGate(muted);
  const auctionAudioControls = { muted, volume, toggleMute, handleVolumeChange };

  const draftIsComplete = draftState?.state === "DRAFT_COMPLETE";

  // [TD-47.6a-ui Gap A.2 + Gap B.1] Canvas state - last-clicked player.
  // Pushed via the triple-callback `onCanvasUpdate` from AuctionPlayerPool.
  // Stays sticky across re-renders so the canvas keeps showing the most
  // recent player until the user clicks a new one.
  const [canvasPlayer, setCanvasPlayer] = useState(null);

  // [TD-47.2a] State pill in top bar center (auction-specific).
  const renderTopBarCenter = () => {
    if (!draftState?.state) return null;
    const stateClass = (() => {
      switch (draftState.state) {
        case "BIDDING": return "bg-[#FF6B00]/20 text-[#FF6B00]";
        case "NOMINATION": return "bg-[#007AFF]/20 text-[#007AFF]";
        case "GOING_ONCE":
        case "GOING_TWICE": return "bg-[#FF4444]/20 text-[#FF4444]";
        case "PAUSED": return "bg-yellow-400/20 text-yellow-400";
        case "DRAFT_COMPLETE": return "bg-[#00C853]/20 text-[#00C853]";
        default: return "bg-[#252525] text-[#A0A0A0]";
      }
    })();
    return (
      <span
        className={`font-display font-bold text-xs uppercase tracking-wider px-2 py-0.5 rounded ${stateClass}`}
        data-testid="auction-state-pill"
      >
        {draftState.state.replace(/_/g, " ")}
      </span>
    );
  };

  // [TD-47.5 Gap 4.A] Right-top "your data" tabs - Roster default, History second.
  const rightTopTabs = [
    {
      id: "roster",
      label: "Roster",
      icon: UsersIcon,
      render: () => (
        <RosterPanel
          draftState={draftState}
          userId={user?.id}
          testIdPrefix="auction-roster-panel"
        />
      ),
    },
    {
      id: "history",
      label: "History",
      icon: HistoryIcon,
      render: () => (
        <PickHistoryTabBody
          draftState={draftState}
          fullHistory={fullPickHistory}
          activityVoice={activityVoice}
          isDark={true}
        />
      ),
    },
  ];

  return (
    <UnifiedDraftShell
      format="auction"
      leagueId={leagueId}
      league={league}
      isConnected={isConnected}
      draftIsComplete={draftIsComplete}
      bridgeRecapUrl={bridgeRecapUrl}
      recapDismissed={recapDismissed}
      dismissRecap={dismissRecap}
      mockDraftCompleteEvent={mockDraftCompleteEvent}
      setMockDraftCompleteEvent={setMockDraftCompleteEvent}
      isCommissioner={isCommissioner}
      boardUrl={`/draft/${leagueId}/board`}
      formatBadgeText={null}
      renderTopBarCenter={renderTopBarCenter}
      renderCommissionerControls={() =>
        isCommissioner && draftState ? (
          <div className="flex items-stretch">
            <CommissionerControls leagueId={leagueId} draftState={draftState} league={league} />
            <MockDraftControls leagueId={leagueId} league={league} draftState={draftState} />
            <DraftRoomAbandonButton league={league} isCommissioner={isCommissioner} isDark={true} />
            <CommissionerManagersDropdown league={league} isCommissioner={isCommissioner} isDark={true} />
          </div>
        ) : null
      }
      renderLeftPane={() => (
        <AuctionPlayerPool
          draftState={draftState}
          leagueId={leagueId}
          onCanvasUpdate={setCanvasPlayer}
          leagueRoster={league?.settings?.roster}
        />
      )}
      renderCenterTop={() => <CompactActionPanel />}
      renderCenterMiddle={() => (
        <PlayerAnalysisCanvas leagueId={leagueId} currentPlayer={canvasPlayer} />
      )}
      renderRightTop={() => (
        <TabbedPanel
          testIdPrefix="auction-right-top-tabs"
          defaultTab="roster"
          tabs={rightTopTabs}
        />
      )}
      renderRightBottom={() => (
        <ChatPanelWithFilter audioControls={auctionAudioControls} />
      )}
      renderMobile={() => <MobilePaddle leagueId={leagueId} league={league} />}
    />
  );
}

export default function DraftRoom() {
  const { id: leagueId } = useParams();
  const nav = useNavigate();
  const [league, setLeague] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const refetch = () => {
      axios.get(`${API}/leagues/${leagueId}`, { headers: authHeaders() })
        .then(r => {
          setLeague(r.data);
          if (r.data.status === "SETUP") nav(`/league/${leagueId}/setup`);
          else if (r.data.status === "LOBBY") nav(`/league/${leagueId}/lobby`);
        })
        .catch(() => nav("/dashboard"))
        .finally(() => setLoading(false));
    };
    refetch();
    // [TD-89 v2 Verdict 8.A] On DRAFT_ABANDONED / MANAGERS_UPDATED re-fetch
    // league. The existing status==="LOBBY" redirect above kicks in cleanly.
    const onAbandon = () => refetch();
    const onMgrs = () => refetch();
    window.addEventListener("pgf:draft-abandoned", onAbandon);
    window.addEventListener("pgf:managers-updated", onMgrs);
    return () => {
      window.removeEventListener("pgf:draft-abandoned", onAbandon);
      window.removeEventListener("pgf:managers-updated", onMgrs);
    };
  }, [leagueId]);

  if (loading) return (
    <div className="min-h-screen bg-[#111111] flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-[#FF6B00] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  return (
    <DraftProvider leagueId={leagueId}>
      <DraftFiltersProvider>
        <DraftRoomInner leagueId={leagueId} league={league} />
      </DraftFiltersProvider>
    </DraftProvider>
  );
}
